import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, Check } from "lucide-react";
import { PRESETS, PRESET_CATEGORIES } from "@/lib/studio-presets";
import { Button } from "@/components/ui/button";

interface FormatPickerContentProps {
  selected: string[];
  onSelectionChange: (labels: string[]) => void;
  filterCategoryLabels?: string[];
  suggestedCategoryLabels?: string[];
}

export function FormatPickerContent({
  selected,
  onSelectionChange,
  filterCategoryLabels,
  suggestedCategoryLabels,
}: FormatPickerContentProps) {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  const categories = useMemo(() => {
    const base = filterCategoryLabels
      ? PRESET_CATEGORIES.filter((c) => filterCategoryLabels.includes(c.label))
      : PRESET_CATEGORIES;
    if (!suggestedCategoryLabels?.length) return base;
    const suggested = base.filter((c) => suggestedCategoryLabels.includes(c.label));
    const rest = base.filter((c) => !suggestedCategoryLabels.includes(c.label));
    return [...suggested, ...rest];
  }, [filterCategoryLabels, suggestedCategoryLabels]);

  const suggestedLabelSet = useMemo(
    () => new Set(suggestedCategoryLabels ?? []),
    [suggestedCategoryLabels]
  );

  const allAllowedLabels = useMemo(
    () => new Set(categories.flatMap((c) => c.presets)),
    [categories]
  );

  const displayPresets = useMemo(() => {
    const trimmed = search.trim().toLowerCase();
    if (trimmed) {
      return PRESETS.filter((p) =>
        allAllowedLabels.has(p.label) &&
        (p.label.toLowerCase().includes(trimmed) ||
          p.hint.toLowerCase().includes(trimmed))
      );
    }
    if (activeCategory === "All") {
      const all = PRESETS.filter((p) => allAllowedLabels.has(p.label));
      if (!suggestedCategoryLabels?.length) return all;
      const suggestedPresetLabels = new Set(
        PRESET_CATEGORIES
          .filter((c) => suggestedLabelSet.has(c.label))
          .flatMap((c) => c.presets)
      );
      const top = all.filter((p) => suggestedPresetLabels.has(p.label));
      const rest = all.filter((p) => !suggestedPresetLabels.has(p.label));
      return [...top, ...rest];
    }
    const cat = PRESET_CATEGORIES.find((c) => c.label === activeCategory);
    const labelsInCat = new Set(cat?.presets ?? []);
    return PRESETS.filter((p) => labelsInCat.has(p.label));
  }, [activeCategory, search, allAllowedLabels, suggestedCategoryLabels, suggestedLabelSet]);

  const toggle = (label: string) => {
    if (selected.includes(label)) {
      onSelectionChange(selected.filter((l) => l !== label));
    } else {
      onSelectionChange([...selected, label]);
    }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
      <div className="relative px-4 py-3 border-b border-border/40 shrink-0">
        <Search className="absolute left-7 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground/50 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search formats..."
          className="w-full pl-8 pr-8 py-2 rounded-lg border border-border/40 bg-secondary/20 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:border-primary/40 transition-colors"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="absolute right-7 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-foreground transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {!search.trim() && (
        <div className="flex gap-1.5 px-4 py-2 overflow-x-auto border-b border-border/20 shrink-0 scrollbar-none">
          <button
            type="button"
            onClick={() => setActiveCategory("All")}
            className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
              activeCategory === "All"
                ? "bg-primary text-primary-foreground"
                : "bg-secondary/30 text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
            }`}
          >
            All
          </button>
          {categories.map((cat) => {
            const isSuggested = suggestedLabelSet.has(cat.label);
            return (
              <button
                key={cat.label}
                type="button"
                onClick={() => setActiveCategory(cat.label)}
                className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                  activeCategory === cat.label
                    ? "bg-primary text-primary-foreground"
                    : isSuggested
                    ? "bg-primary/10 text-primary/80 border border-primary/20 hover:bg-primary/15 hover:text-primary"
                    : "bg-secondary/30 text-muted-foreground hover:bg-secondary/60 hover:text-foreground"
                }`}
              >
                {cat.label}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4">
        {displayPresets.length === 0 ? (
          <p className="text-sm text-muted-foreground/50 text-center py-8">
            No formats found{search ? ` for "${search}"` : ""}.
          </p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {displayPresets.map((preset) => {
              const isSelected = selected.includes(preset.label);
              const Icon = preset.icon;
              return (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => toggle(preset.label)}
                  className={`flex items-start gap-2.5 p-3 rounded-xl border text-left transition-all relative ${
                    isSelected
                      ? "border-primary bg-primary/8 shadow-sm"
                      : "border-border/40 bg-secondary/10 hover:border-border hover:bg-secondary/30"
                  }`}
                >
                  <div
                    className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                      isSelected ? "bg-primary/15" : "bg-secondary/50"
                    }`}
                  >
                    <Icon
                      className={`w-3.5 h-3.5 ${
                        isSelected ? "text-primary" : "text-muted-foreground"
                      }`}
                    />
                  </div>
                  <div className="min-w-0 flex-1 pr-4">
                    <p
                      className={`text-[11px] font-semibold leading-tight ${
                        isSelected ? "text-primary" : "text-foreground"
                      }`}
                    >
                      {preset.label}
                    </p>
                    <p className="text-[10px] text-muted-foreground leading-snug mt-0.5 line-clamp-2">
                      {preset.hint.replace(/^e\.g\.\s*/i, "")}
                    </p>
                  </div>
                  {isSelected && (
                    <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-primary flex items-center justify-center shrink-0">
                      <Check className="w-2.5 h-2.5 text-primary-foreground" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

interface FormatPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  selected: string[];
  onSelectionChange: (labels: string[]) => void;
  suggestedCategoryLabels?: string[];
}

export function FormatPickerModal({
  isOpen,
  onClose,
  selected,
  onSelectionChange,
  suggestedCategoryLabels,
}: FormatPickerModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="format-picker-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex flex-col items-end justify-end md:items-center md:justify-center md:p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 60 }}
            transition={{ type: "spring", stiffness: 280, damping: 28 }}
            className="w-full md:max-w-2xl bg-card border-t border-border md:border md:rounded-2xl rounded-t-3xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: "88svh" }}
          >
            <div className="shrink-0 w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mt-3 mb-1 md:hidden" />

            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border/40 shrink-0">
              <div>
                <h3 className="font-bold text-base">Add Formats</h3>
                <p className="text-xs text-muted-foreground">
                  {selected.length > 0
                    ? `${selected.length} selected`
                    : "Pick what you write most"}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-secondary/50 transition-colors"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            <FormatPickerContent
              selected={selected}
              onSelectionChange={onSelectionChange}
              suggestedCategoryLabels={suggestedCategoryLabels}
            />

            <div className="shrink-0 px-4 py-3.5 border-t border-border/40">
              <Button
                className="w-full h-11 rounded-xl font-bold bg-gradient-to-r from-primary to-accent hover:opacity-90"
                onClick={onClose}
              >
                {selected.length > 0
                  ? `Save ${selected.length} Format${selected.length !== 1 ? "s" : ""}`
                  : "Done"}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
